import React, { useEffect, useState } from "react";
import { getAllUsers } from "../../../../api/api.js";
import { normalizeIsFired } from "../../../../utils/buildUserUpdatePayload";
import Preloader from "../../../Preloader/Preloader.js";
import Select from "react-select";
import "./Users.css";

const getFiredStatusLabel = (user) =>
  normalizeIsFired(user.is_fired) ? "Уволен" : "Не уволен";

const formatFiredStatus = (user) => {
  const label = getFiredStatusLabel(user);
  if (label === "Не уволен" || !user.is_fired_date) return label;
  const date = String(user.is_fired_date).split("T")[0].substring(0, 10);
  const [y, m, d] = date.split("-");
  return y && m && d ? `${label} (${d}.${m}.${y})` : label;
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blurStates, setBlurStates] = useState({});
  const [filters, setFilters] = useState({
    name: [],
    department: [],
    position: [],
    email: [],
    username: [],
    status: [],
  });
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const getUsers = async () => {
      try {
        const data = await getAllUsers();
        setUsers(data);
        setFilteredUsers(data);
        const initialBlurStates = data.reduce((acc, user) => {
          acc[user.id] = true;
          return acc;
        }, {});
        setBlurStates(initialBlurStates);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter((user) => {
      return Object.entries(filters).every(([key, values]) => {
        if (values.length === 0) return true;
        if (key === "name") {
          const fullName = `${user.surname} ${user.name} ${user.patronymic}`
            .trim()
            .toLowerCase();
          return values.some((value) => fullName.includes(value.toLowerCase()));
        }
        if (key === "department") {
          return (
            user.groups &&
            user.groups.some((group) =>
              values.some((value) =>
                group.name.toLowerCase().includes(value.toLowerCase())
              )
            )
          );
        }
        if (key === "position") {
          return (
            user.groups &&
            user.groups.some((group) =>
              group.roles.some((role) =>
                values.some((value) =>
                  role.name.toLowerCase().includes(value.toLowerCase())
                )
              )
            )
          );
        }
        if (key === "status") {
          return values.includes(getFiredStatusLabel(user));
        }
        return values.some((value) =>
          user[key]?.toString().toLowerCase().includes(value.toLowerCase())
        );
      });
    });
    setFilteredUsers(filtered);
  }, [filters, users]);

  const toggleBlur = (userId) => {
    setBlurStates((prevState) => ({
      ...prevState,
      [userId]: !prevState[userId],
    }));
  };

  const formatMultipleValues = (array, key) => {
    if (!array || !Array.isArray(array)) return "Не указано";
    return (
      array
        .map((item) => (typeof item === "object" ? item[key] : item))
        .join(" \n ") || "Не указано"
    );
  };

  const formatSalary = (salary) => {
    if (!salary) return "Не указано";
    return salary.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "Не указано";
    return phoneNumber.replace(
      /(\+\d)(\d{3})(\d{3})(\d{2})(\d{2})/,
      "$1 ($2) $3-$4-$5"
    );
  };

  const getUniqueValues = (key) => {
    return [
      ...new Set(
        users
          .flatMap((user) => {
            if (key === "name")
              return `${user.surname} ${user.name} ${user.patronymic}`.trim();
            if (key === "department")
              return user.groups ? user.groups.map((group) => group.name) : [];
            if (key === "position")
              return user.groups
                ? user.groups.flatMap((group) =>
                    group.roles.map((role) => role.name)
                  )
                : [];
            if (key === "status") return getFiredStatusLabel(user);
            return user[key]?.toString();
          })
          .filter(Boolean)
      ),
    ];
  };

  const handleFilterChange = (column, selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [column]: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "100%",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: "#E0E0E0",
      borderRadius: "5px",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      fontSize: "12px",
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: "#666",
      ":hover": {
        backgroundColor: "#ccc",
        color: "#333",
      },
    }),
  };

  const getFilterLabel = (column) => {
    const labels = {
      name: "ФИО",
      department: "Отдел",
      position: "Должность",
      email: "E-mail",
      status: "Увольнение",
    };
    return labels[column] || column;
  };

  const renderFilterInput = (column) => {
    if (["salary", "birth_date", "phone_number"].includes(column)) {
      return getFilterLabel(column);
    }

    const options = getUniqueValues(column).map((value) => ({
      value: value,
      label: value,
    }));

    return (
      <Select
        isMulti
        options={options}
        styles={customStyles}
        value={filters[column].map((value) => ({ value, label: value }))}
        onChange={(selectedOptions) =>
          handleFilterChange(column, selectedOptions)
        }
        placeholder={getFilterLabel(column)}
        noOptionsMessage={() => "Нет доступных опций"}
      />
    );
  };

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div>Ошибка: {error}</div>;
  }

  return (
    <div className="users-container">
      <h1>Все пользователи</h1>
      <div className="users-table-wrapper">
        <table className="users">
          <thead>
            <tr>
              <th>{renderFilterInput("name")}</th>
              <th>{renderFilterInput("department")}</th>
              <th>{renderFilterInput("position")}</th>
              <th>{renderFilterInput("email")}</th>
              <th>{renderFilterInput("status")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr className="users__points" key={user.id}>
                <td className="users__point">{`${user.surname || ""} ${
                  user.name || ""
                } ${user.patronymic || ""}`}</td>
                <td className="users__point">
                  {formatMultipleValues(user.groups, "name")}
                </td>
                <td className="users__point">
                  {formatMultipleValues(
                    user.groups?.flatMap((group) => group.roles),
                    "name"
                  )}
                </td>
                <td className="users__point">{user.email || "Не указано"}</td>
                <td className="users__point">{formatFiredStatus(user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
